import React, { useMemo } from "react";
import CommonButton from "../../CommonButton";
import { Equal, Plus } from "lucide-react";
import { BundleProduct } from "../../../types/Response/CustomerResponse";

interface Props {
    bundleData: BundleProduct
    onAddBundle: (products: BundleProduct) => void;
    className?: string;
    bgClass?: string;
}

const FrequentlyBought: React.FC<Props> = ({
    bundleData,
    onAddBundle,
    className = "",
    bgClass = "bg-gray-100"
}) => {
    const productsList = bundleData?.products || [];

    // Calculate sum price of standalone items
    const originalTotalPrice = useMemo(() => {
        return productsList.reduce((acc, item) => acc + (item.price || 0), 0);
    }, [productsList]);

    const bundleSalePrice = bundleData?.bundlePrice || originalTotalPrice;

    // Calculate final bundle price savings percentage
    const discountPercent = useMemo(() => {
        if (originalTotalPrice <= 0 || bundleSalePrice >= originalTotalPrice) return 0;
        return Math.round(((originalTotalPrice - bundleSalePrice) / originalTotalPrice) * 100);
    }, [originalTotalPrice, bundleSalePrice]);

    if (productsList.length === 0) return null;

    return (
        <div
            className={`
                ${bgClass}
                border border-gray-200
                rounded-2xl
                px-5 py-4
                w-full
                text-left
                ${className}
            `}
        >
            {/* TITLE */}
            <h3 className="text-center font-bold text-slate-800 text-lg md:text-xl mb-6">
                {bundleData.name || "Frequently Bought Together"}
            </h3>

            {/* LAYOUT CONTAINER ROW */}
            <div className="w-full overflow-x-auto lg:overflow-visible">
                <div
                    className="
                        flex items-center gap-4 md:gap-6 lg:gap-16
                        flex-nowrap lg:flex-wrap
                        lg:justify-center
                        min-w-max lg:min-w-0
                    "
                >
                    {/* PRODUCTS DISPLAY LIST */}
                    {productsList.map((item, index) => (
                        <React.Fragment key={item.productId || index}>
                            <div className="flex flex-col items-center flex-shrink-0 w-[100px] sm:w-[130px] md:w-[160px]">
                                
                                {/* PRODUCT IMAGE */}
                                <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-3xs w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 flex items-center justify-center p-2">
                                    <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                        className="max-w-full max-h-full object-contain"
                                    />
                                </div>

                                {/* NAME */}
                                <p className="text-[10px] sm:text-xs md:text-sm mt-2.5 text-center font-bold text-slate-700 line-clamp-2 min-h-[32px]">
                                    {item.name}
                                </p>
                            </div>

                            {/* PLUS SEPARATOR */}
                            {index < productsList.length - 1 && (
                                <Plus className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                            )}
                        </React.Fragment>
                    ))}

                    {/* EQUAL ICON */}
                    <Equal className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />

                    {/* PRICING CONTROL SECTION */}
                    <div className="flex flex-col items-center md:items-start gap-2.5 flex-shrink-0">
                        <div className="flex flex-col">
                            <span className="text-[10px] uppercase font-black text-gray-400 tracking-wider">Bundle Price</span>
                            <div className="flex items-baseline gap-2 mt-0.5">
                                <span className="text-[#2F5E2E] font-black text-xl md:text-2xl">
                                    AED {bundleSalePrice.toFixed(2)}
                                </span>
                                {discountPercent > 0 && (
                                    <span className="line-through text-gray-400 text-xs md:text-sm">
                                        AED {originalTotalPrice.toFixed(2)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* SUBMIT BUTTON */}
                        <CommonButton
                            onClick={() => onAddBundle(bundleData)}
                            text="Add Bundle"
                            variant="filled"
                            rounded="full"
                            className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white px-6 text-xs h-9 font-bold"
                        />

                        {/* DYNAMIC DISCOUNT PERCENT BADGE */}
                        {discountPercent > 0 && (
                            <p className="text-[10px] font-black text-rose-600 uppercase bg-rose-50 px-2 py-0.5 rounded-md tracking-wider">
                                SAVE {discountPercent}% OFF
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FrequentlyBought;